import React from 'react';
import {
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Button,
  Card,
  Badge,
  Box,
  Divider,
  ThemeIcon,
  Center,
  Container,
  Transition,
} from '@mantine/core';
import { createStyles } from '@mantine/styles';
import {
  IconArrowRight,
  IconCpu,
  IconSpeedboat,
  IconScale,
  IconFileAnalytics,
  IconCheck,
  IconBolt,
  IconDeviceLaptop,
} from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
  wrapper: {
    background:
      'linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(76, 201, 240, 0.1))',
    backdropFilter: 'blur(20px)',
    borderRadius: 'var(--border-radius-lg)',
    padding: '40px 20px',
    boxShadow: 'var(--shadow-md)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    overflow: 'hidden',
    position: 'relative',
    maxWidth: '1400px',
    margin: '0 auto',
    '@media (min-width: 992px)': {
      padding: '40px 60px',
    },
    '@media (min-width: 1200px)': {
      padding: '40px 80px',
    },
    '[data-theme="dark"] &': {
      background:
        'linear-gradient(135deg, rgba(67, 97, 238, 0.1), rgba(76, 201, 240, 0.15))',
      border: '1px solid rgba(30, 41, 59, 0.5)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '6px',
      background:
        'linear-gradient(90deg, var(--primary-color), var(--secondary-color))',
      zIndex: 1,
    },
  },
  pageTitle: {
    fontSize: '3rem',
    fontWeight: 800,
    textAlign: 'center',
    marginBottom: '10px',
    background:
      'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textShadow: '0 5px 15px rgba(67, 97, 238, 0.2)',
    letterSpacing: '-0.5px',
    '@media (max-width: 576px)': {
      fontSize: '2rem',
    },
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontWeight: 500,
    textAlign: 'center',
    maxWidth: '700px',
    margin: '0 auto 40px',
    fontSize: '1.1rem',
    lineHeight: 1.6,
    '@media (max-width: 576px)': {
      fontSize: '1rem',
      marginBottom: '30px',
    },
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '24px',
    width: '100%',
    '@media (max-width: 576px)': {
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    },
  },
  modelCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 0,
    overflow: 'hidden',
    transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 'var(--border-radius-lg)',
    boxShadow: 'var(--shadow-sm)',
    border: '1px solid rgba(241, 245, 249, 0.8)',
    cursor: 'pointer',
    transform: 'translateY(0)',
    '[data-theme="dark"] &': {
      backgroundColor: 'rgba(30, 41, 59, 0.8)',
      border: '1px solid rgba(30, 41, 59, 0.8)',
    },
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(255, 255, 255, 1)',
      '[data-theme="dark"] &': {
        border: '1px solid rgba(67, 97, 238, 0.5)',
      },
    },
  },
  cardHeader: {
    position: 'relative',
    padding: '24px 24px 12px',
    background: 'var(--card-gradient)',
    borderBottom: '1px solid rgba(241, 245, 249, 0.5)',
  },
  cardBody: {
    padding: '16px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  selectedCard: {
    boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
    borderColor: 'var(--primary-color)',
    transform: 'translateY(-8px)',
    transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'var(--border-radius-lg)',
      padding: '2px',
      background:
        'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
      WebkitMask:
        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      zIndex: 0,
      transition: 'opacity 0.5s ease',
    },
    '& .selected-label': {
      display: 'flex',
      animation: 'fadeIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    '&:hover': {
      borderColor: 'var(--primary-color)',
      boxShadow: '0 15px 30px rgba(67, 97, 238, 0.2)',
    },
  },
  selectedLabel: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    padding: '4px 12px',
    borderRadius: 'var(--border-radius-full)',
    fontSize: '0.85rem',
    fontWeight: 600,
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    zIndex: 2,
    boxShadow: '0 2px 10px rgba(67, 97, 238, 0.3)',
    animation: 'fadeIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    background: 'linear-gradient(90deg, var(--primary-color), #5c7cfa)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
  },
  modelName: {
    fontSize: '1.8rem',
    fontWeight: 700,
    margin: 0,
    color: 'var(--text-primary)',
  },
  precisionBadge: {
    backgroundColor: 'rgba(46, 204, 113, 0.1)',
    color: '#2ecc71',
    padding: '6px 14px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.9rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  modelDescription: {
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    fontSize: '1rem',
    marginBottom: '24px',
  },
  featureWrapper: {
    marginTop: 'auto',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  featureIcon: {
    color: 'var(--primary-color)',
    backgroundColor: 'rgba(67, 97, 238, 0.1)',
  },
  featureText: {
    color: 'var(--text-secondary)',
    fontSize: '0.95rem',
  },
  sizeBadge: {
    backgroundColor: 'rgba(100, 116, 139, 0.1)',
    color: 'var(--text-secondary)',
    padding: '6px 14px',
    borderRadius: 'var(--border-radius-full)',
    fontSize: '0.9rem',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    margin: '20px auto 0',
  },
  continueButton: {
    marginTop: '0',
    width: '100%',
    maxWidth: '400px',
    padding: '16px',
    fontSize: '1.1rem',
    borderRadius: 'var(--border-radius-lg)',
    fontWeight: 600,
    transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    background:
      'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
    border: 'none',
    color: 'white',
    boxShadow: 'var(--shadow-sm)',
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    transform: 'translateY(0)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background:
        'linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0))',
      transform: 'translateX(-100%)',
      transition: 'transform 0.6s ease',
    },
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 15px 30px rgba(67, 97, 238, 0.2)',
      '&::before': {
        transform: 'translateX(100%)',
      },
    },
    '&:disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
      background: 'linear-gradient(45deg, #94a3b8, #cbd5e1)',
    },
  },
}));

function ModelSelector({ onModelSelect }) {
  const { classes, cx } = useStyles();
  const [selectedModel, setSelectedModel] = React.useState(null);

  const models = [
    {
      id: 'resnet34_float32',
      name: 'ResNet34',
      path: 'resnet34/tfjs_model_float32',
      precision: 'Full Precision (float32)',
      description:
        'A lighter model with good accuracy and faster inference. Uses full 32-bit floating point precision for maximum accuracy.',
      features: ['34 Layers', 'Fast Inference', 'Balanced Performance'],
      size: '~21MB',
      icon: <IconSpeedboat size={20} stroke={1.5} />,
    },
    {
      id: 'resnet34_float16',
      name: 'ResNet34',
      path: 'resnet34/tfjs_model_float16',
      precision: 'Reduced Precision (float16)',
      description:
        'Same ResNet34 architecture but with 16-bit floating point precision, offering faster inference with minimal accuracy loss.',
      features: ['34 Layers', 'Faster Inference', 'Smaller Size'],
      size: '~11MB',
      icon: <IconBolt size={20} stroke={1.5} />,
    },
    {
      id: 'resnet152_float32',
      name: 'ResNet152',
      path: 'resnet152/tfjs_model_float32',
      precision: 'Full Precision (float32)',
      description:
        'A deeper model with higher accuracy but slower inference. Uses full 32-bit floating point precision for maximum accuracy.',
      features: ['152 Layers', 'Higher Accuracy', 'More Complex'],
      size: '~60MB',
      icon: <IconFileAnalytics size={20} stroke={1.5} />,
    },
    {
      id: 'resnet152_float16',
      name: 'ResNet152',
      path: 'resnet152/tfjs_model_float16',
      precision: 'Reduced Precision (float16)',
      description:
        'The deep ResNet152 architecture with 16-bit floating point precision, balancing accuracy and performance for edge devices.',
      features: ['152 Layers', 'Better Performance', 'Reduced Size'],
      size: '~30MB',
      icon: <IconDeviceLaptop size={20} stroke={1.5} />,
    },
  ];

  const handleContinue = () => {
    if (selectedModel) {
      onModelSelect(models.find((m) => m.id === selectedModel));
    }
  };

  // Map feature names to icons
  const getFeatureIcon = (feature) => {
    if (feature.includes('Layer')) return <IconCpu size={18} />;
    if (feature.includes('Fast') || feature.includes('Performance'))
      return <IconSpeedboat size={18} />;
    if (feature.includes('Balance')) return <IconScale size={18} />;
    return <IconCheck size={18} />;
  };

  return (
    <Container size="xl" p={0} style={{ width: '100%', padding: '0 20px' }}>
      <Box className={classes.wrapper}>
        <Stack spacing="xl" align="center" className="animate-fadeIn">
          <Title className={classes.pageTitle}>Select Your Model</Title>

          <Text className={classes.subtitle}>
            Choose the facial recognition model that best suits your needs.
            Different models offer trade-offs between accuracy, speed, and size.
          </Text>

          <div className={classes.cardsContainer}>
            {models.map((model) => (
              <Transition
                key={model.id}
                mounted={true}
                transition="fade"
                duration={400}
                timingFunction="ease"
              >
                {(styles) => (
                  <Card
                    style={styles}
                    className={cx(classes.modelCard, {
                      [classes.selectedCard]: selectedModel === model.id,
                    })}
                    onClick={() => setSelectedModel(model.id)}
                    shadow="sm"
                  >
                    <div
                      className={cx('selected-label', classes.selectedLabel)}
                    >
                      <IconCheck size={14} stroke={2.5} /> Selected
                    </div>

                    <div className={classes.cardHeader}>
                      <Group position="apart" align="center">
                        <Text className={classes.modelName}>{model.name}</Text>
                        {model.icon}
                      </Group>
                      <Badge className={classes.precisionBadge}>
                        {model.precision}
                      </Badge>
                    </div>

                    <div className={classes.cardBody}>
                      <Text className={classes.modelDescription}>
                        {model.description}
                      </Text>

                      <div className={classes.featureWrapper}>
                        {model.features.map((feature, index) => (
                          <div key={index} className={classes.featureItem}>
                            <ThemeIcon
                              size="sm"
                              radius="xl"
                              className={classes.featureIcon}
                            >
                              {getFeatureIcon(feature)}
                            </ThemeIcon>
                            <Text className={classes.featureText}>
                              {feature}
                            </Text>
                          </div>
                        ))}
                      </div>

                      <Center>
                        <Badge
                          leftSection={<IconCpu size={14} />}
                          className={classes.sizeBadge}
                        >
                          {model.size}
                        </Badge>
                      </Center>
                    </div>
                  </Card>
                )}
              </Transition>
            ))}
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '40px',
            }}
          >
            <Button
              size="lg"
              disabled={!selectedModel}
              onClick={handleContinue}
              className={classes.continueButton}
              style={{
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
              rightSection={
                selectedModel ? <IconArrowRight size={20} stroke={2} /> : null
              }
            >
              Continue with Selected Model
            </Button>
          </div>
        </Stack>
      </Box>
    </Container>
  );
}

export default ModelSelector;
